import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowLeft, Calendar, MapPin, Tag, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';


export default function BookingHistoryPage() {
  const { user, token } = useAuth();
  const { t, lang } = useLang();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/api/bookings/history`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setBookings(data.bookings || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (!user) {
    return (
      <div className="min-h-screen pt-14 flex flex-col items-center justify-center gap-4">
        <p className="text-pulse-muted">{lang === 'es' ? 'Inicia sesion para ver tu historial' : 'Login to view history'}</p>
        <Link to="/login" className="bg-pulse-primary text-black px-6 py-2 rounded-xl font-semibold">{t('auth.login')}</Link>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen pt-14 flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const statusColor = (s) => {
    if (s === 'confirmed') return 'bg-green-500/20 text-green-400';
    if (s === 'cancelled') return 'bg-red-500/20 text-red-400';
    return 'bg-yellow-500/20 text-yellow-400';
  };

  const statusLabel = (s) => {
    if (lang === 'es') {
      if (s === 'confirmed') return 'Confirmado';
      if (s === 'cancelled') return 'Cancelado';
      return 'Pendiente';
    }
    if (s === 'confirmed') return 'Confirmed';
    if (s === 'cancelled') return 'Cancelled';
    return 'Pending';
  };

  return (
    <div data-testid="booking-history-page" className="min-h-screen pt-14 pb-32">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Link to="/profile" className="flex items-center gap-2 text-pulse-muted hover:text-white mb-6 transition-colors duration-200">
          <ArrowLeft size={18} /> {t('common.back')}
        </Link>

        <h1 className="font-heading font-bold text-2xl mb-6 flex items-center gap-3">
          <Clock size={22} className="text-pulse-primary" />
          {t('booking.history')}
        </h1>

        {bookings.length === 0 ? (
          <div className="text-center py-16">
            <Clock size={48} className="text-pulse-border mx-auto mb-4" />
            <p className="text-pulse-muted">{lang === 'es' ? 'No tienes contrataciones aun' : 'No bookings yet'}</p>
            <Link to="/" className="inline-block mt-4 bg-pulse-primary text-black px-6 py-2 rounded-xl font-semibold text-sm">
              {lang === 'es' ? 'Explorar Artistas' : 'Explore Artists'}
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {bookings.map(b => (
              <div key={b.id} data-testid={`booking-item-${b.id}`} className="bg-pulse-surface border border-pulse-border rounded-xl p-4 hover:border-cyan-500/20 transition-colors duration-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {b.artist_photo && <img src={b.artist_photo} alt={b.artist_name} className="w-12 h-12 rounded-lg object-cover" />}
                    <div>
                      <p className="font-heading font-semibold">{b.artist_name}</p>
                      <p className="text-xs text-pulse-muted">{b.price}€</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor(b.status)}`}>
                    {statusLabel(b.status)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-pulse-muted">
                  <span className="flex items-center gap-1"><Tag size={10} /> {b.event_type || '-'}</span>
                  <span className="flex items-center gap-1"><Calendar size={10} /> {b.date || '-'}</span>
                  <span className="flex items-center gap-1"><MapPin size={10} /> {b.location || '-'}</span>
                </div>
                {b.message && <p className="text-xs text-pulse-muted mt-2 italic">"{b.message}"</p>}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-pulse-border">
                  <span className="text-[10px] text-pulse-muted">{new Date(b.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <Link to={`/booking/${b.artist_id}`} className="text-xs text-pulse-primary flex items-center gap-1 hover:underline">
                    <MessageCircle size={12} /> {lang === 'es' ? 'Ver chat' : 'View chat'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
